import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Geoposition } from '@awesome-cordova-plugins/geolocation/ngx';
import { Coordinate } from '@models/coordinate';
import { Guid } from '@models/guid';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LocationProxyEvent } from '../../../schema/enum/location-proxy-event';
import { SETTING_PROVIDER } from '../../../schema/injection-token';
import { ILocationProxy } from '../../../schema/interfaces/location-proxy';
import { ISettingProvider } from '../../../schema/interfaces/settings-provider';

@Injectable()
export class PositionProxyService implements ILocationProxy {
  protected isLocationTrackingEnabled: boolean = true;
  protected _interval: any = null;
  protected lastPositionSend: number = null;
  protected sendPositionTimeout: number = null;
  protected lastPosition: Geoposition = null;

  protected _resourceUid: Guid = null;

  protected _lastError: any = null;

  protected readonly _state = new BehaviorSubject<LocationProxyEvent>(null);
  get $event(): Observable<LocationProxyEvent> {
    return this._state.asObservable();
  }

  protected readonly _isRunning = new BehaviorSubject(false);
  get $state(): Observable<boolean> {
    return this._isRunning.asObservable();
  }

  get isRunning(): boolean {
    let v: boolean;
    this._isRunning.subscribe(val => (v = val)).unsubscribe();
    return v;
  }

  protected readonly _epsg = 4326;
  protected readonly _empty: Coordinate = { x: 0, y: 0, epsg: `EPSG:${this._epsg}` };
  get empty(): Coordinate {
    return this._empty;
  }

  constructor(
    @Inject(SETTING_PROVIDER)
    private settingsProvider: ISettingProvider,
    private httpClient: HttpClient
  ) {}

  init(uid: Guid) {
    this.sendPositionTimeout = this.settingsProvider.getValue('SagaMobileWebClient.SendPositionInterval') || null;
    this.isLocationTrackingEnabled = this.settingsProvider.getValue('SagaMobileWebClient.DefaultLocationTracking') || false;
    this._resourceUid = uid;
  }

  /**
   * Flag the service as started if it is allowed to be started.
   */
  start() {
    if (this.isLocationTrackingEnabled && this._interval === null) {
      this._isRunning.next(true);
    }

    // If the position is known along with the resource id, we start the timer.
    if (this.isLocationTrackingEnabled && this._resourceUid !== null && this.lastPosition !== null) {
      this.updatePosition();
    }
  }

  stop() {
    clearTimeout(this._interval);
    this._interval = null;
    this._isRunning.next(false);
  }

  update(position: Geoposition) {
    this.lastPosition = position;
    if (position && position !== this.lastPosition && this.isRunning) {
      this.updatePosition();
    }
  }

  private error(err: any, caught: Observable<string>): Observable<string> {
    this._lastError = err;
    this._state.next(LocationProxyEvent.error);
    return of(null);
  }

  private setResourcePosition(position: Coordinate) {
    this._state.next(LocationProxyEvent.sending);
    return this.httpClient
      .post('api/Location/ForVehicle', { resourceUid: this._resourceUid, coordX: position.x, coordY: position.y }, { responseType: 'text' })
      .pipe(
        tap(() => this._state.next(LocationProxyEvent.idle)),
        catchError((err: any, caught: Observable<string>) => this.error(err, caught))
      );
  }

  private transform(p: Geoposition = null): Coordinate {
    return p !== null ? { x: p.coords.longitude, y: p.coords.latitude, epsg: this.empty.epsg } : this.empty;
  }

  private updatePosition() {
    if (this._interval !== null) {
      return;
    }
    const p = this.transform(this.lastPosition);
    const isValid = p && p.x !== 0 && p.y !== 0;

    // Send the position if it is valid and more recent than the last sent position.
    if (isValid && this.lastPositionSend !== this.lastPosition.timestamp) {
      this.setResourcePosition(p).subscribe(() => (this.lastPositionSend = this.lastPosition.timestamp));
    }

    // Starts the timer.
    if (isValid) {
      this._interval = setTimeout(
        () => {
          this._interval = null;
          this.updatePosition();
        },
        this.sendPositionTimeout !== null ? this.sendPositionTimeout * 1000 : 0
      );
    }
  }
}
